// Compiled in-memory search index across all key datasets + static hubs.
// Used by /search?q= for site-wide lookup with simple relevance ranking.

import { NOTARY_ACTS } from "@/data/notary-acts";
import { VISA_TYPES } from "@/data/visa-types";
import { TRANSLATION_LANGUAGES } from "@/data/translation-languages";
import { TRANSLATION_DOCTYPES } from "@/data/translation-doctypes";
import { APOSTILLE_COUNTRIES } from "@/data/apostille-countries";
import { EMBASSY_COUNTRIES } from "@/data/embassies";
import { MARRIAGE_NATIONALITIES } from "@/data/marriage-nationalities";
import { locations } from "@/data/locations";
import { BLOG_POSTS } from "@/data/blog";
import { QUICK_ANSWERS } from "@/data/quick-answers";

export type SearchCategory =
  | "Service"
  | "Notary"
  | "Visa"
  | "Translate"
  | "Apostille"
  | "Embassy"
  | "Marriage"
  | "Location"
  | "Blog"
  | "FAQ"
  | "Page";

export interface SearchEntry {
  category: SearchCategory;
  title: string;
  description: string;
  href: string;
  keywords: string; // searchable haystack (lowercased)
}

const STATIC_PAGES: SearchEntry[] = [
  ["Service", "บริการทั้งหมด", "ภาพรวมบริการทุกประเภทของ NYC Legal", "/services"],
  ["Service", "รับรองเอกสาร MFA / สถานทูต", "บริการรับรองกงสุล กระทรวงการต่างประเทศ และ 40+ สถานทูต", "/embassy"],
  ["Service", "Apostille", "บริการ Apostille สำหรับประเทศสมาชิก Hague Convention", "/apostille"],
  ["Service", "Notary Public", "ทนายผู้รับรองลายมือชื่อและเอกสาร 6 ทนายในสำนักงาน", "/notary"],
  ["Service", "แปลเอกสาร 50+ ภาษา", "แปลเอกสารราชการ NAATI ISO 17100", "/translate"],
  ["Service", "วีซ่าทุกประเภท", "Non-B, LTR, Smart, Elite, DTV, Marriage, Retirement", "/visa"],
  ["Service", "จดทะเบียนสมรสต่างชาติ", "บริการครบวงจร 60+ สัญชาติ", "/marriage"],
  ["Service", "Checklist เตรียมเอกสารวีซ่า", "รายการเอกสารต่อประเภทวีซ่า", "/checklist"],
  ["Page", "ราคา", "ตารางราคาบริการแปลและรับรอง", "/pricing"],
  ["Page", "FAQ คำถามที่พบบ่อย", "30+ คำถามยอดนิยม", "/faq"],
  ["Page", "ถามด่วน (Ask)", "Quick Answers สำหรับ AI Search", "/ask"],
  ["Page", "รีวิวลูกค้า", "2,147+ เคสจริง · ลูกค้าทั่วประเทศ", "/reviews"],
  ["Page", "ทีมงาน", "6 ทนาย Notary + ผู้แปลรับอนุญาต", "/about/team"],
  ["Page", "Trust & Credentials", "ใบรับรอง ISO, NAATI, MoJ", "/trust"],
  ["Page", "Industries", "โซลูชันแต่ละอุตสาหกรรม", "/industries"],
  ["Page", "Blog / Knowledge Hub", "บทความเชิงลึก SEO", "/blog"],
  ["Page", "Case Studies", "เคสจริงที่เราเคยทำ", "/case-studies"],
  ["Page", "Guides", "คู่มือทีละขั้นตอน", "/guides"],
  ["Page", "ติดต่อ / ขอใบเสนอราคา", "ตอบใน 15 นาที", "/quote"],
  ["Page", "Locations / สาขา", "สาขาและพื้นที่บริการ", "/locations"],
  ["Page", "Compare บริการ", "เปรียบเทียบแพ็กเกจ", "/compare"],
  ["Page", "Visa Comparison", "เปรียบเทียบวีซ่าแต่ละประเภท", "/visa-comparison"],
].map(([category, title, description, href]) => ({
  category: category as SearchCategory,
  title,
  description,
  href,
  keywords: `${title} ${description} ${href}`.toLowerCase(),
}));

function build(): SearchEntry[] {
  const entries: SearchEntry[] = [...STATIC_PAGES];

  for (const n of NOTARY_ACTS) {
    entries.push({
      category: "Notary",
      title: `${n.nameTh} (${n.nameEn})`,
      description: `Notary · เริ่ม ฿${n.priceFromTh.toLocaleString()} · ${n.turnaround}`,
      href: `/notary/${n.slug}`,
      keywords: `notary ${n.slug} ${n.nameTh} ${n.nameEn} ${n.useCases.join(" ")}`.toLowerCase(),
    });
  }

  for (const v of VISA_TYPES) {
    entries.push({
      category: "Visa",
      title: `${v.code} — ${v.nameTh}`,
      description: v.tagline,
      href: `/visa`,
      keywords: `visa ${v.code} ${v.name} ${v.nameTh} ${v.tagline}`.toLowerCase(),
    });
  }

  for (const l of TRANSLATION_LANGUAGES) {
    entries.push({
      category: "Translate",
      title: `แปลภาษา${l.nameTh}`,
      description: `แปลเอกสารราชการ ภาษา${l.nameTh}`,
      href: `/translate/${l.slug}`,
      keywords: `translate ${l.slug} ${l.nameTh}`.toLowerCase(),
    });
  }

  for (const d of TRANSLATION_DOCTYPES) {
    entries.push({
      category: "Translate",
      title: `แปล${d.nameTh} (${d.nameEn})`,
      description: d.description,
      href: `/translate`,
      keywords: `translate doctype ${d.slug} ${d.nameTh} ${d.nameEn} ${d.keywordsTh.join(" ")}`.toLowerCase(),
    });
  }

  for (const c of APOSTILLE_COUNTRIES) {
    entries.push({
      category: "Apostille",
      title: `Apostille → ${c.nameTh} (${c.nameEn})`,
      description: `Apostille สำหรับใช้ที่ ${c.nameTh}`,
      href: `/apostille/${c.slug}`,
      keywords: `apostille ${c.slug} ${c.nameTh} ${c.nameEn}`.toLowerCase(),
    });
  }

  for (const e of EMBASSY_COUNTRIES) {
    entries.push({
      category: "Embassy",
      title: `รับรองสถานทูต ${e.nameTh}`,
      description: `Legalization ที่สถานทูต${e.nameTh}ในกรุงเทพ`,
      href: `/embassy/${e.slug}`,
      keywords: `embassy ${e.slug} ${e.nameTh} ${e.nameEn}`.toLowerCase(),
    });
  }

  for (const m of MARRIAGE_NATIONALITIES) {
    entries.push({
      category: "Marriage",
      title: `จดทะเบียนสมรส${m.nameTh}`,
      description: `คู่สมรส ${m.countryTh} ${m.flag}`,
      href: `/marriage/${m.slug}`,
      keywords: `marriage ${m.slug} ${m.nameTh} ${m.countryTh} ${m.countryEn}`.toLowerCase(),
    });
  }

  for (const loc of locations) {
    entries.push({
      category: "Location",
      title: loc.nameTh,
      description: `${loc.nameEn} · ${loc.region}`,
      href: `/locations/${loc.slug}`,
      keywords: `location ${loc.slug} ${loc.nameTh} ${loc.nameEn} ${loc.region}`.toLowerCase(),
    });
  }

  for (const p of BLOG_POSTS) {
    entries.push({
      category: "Blog",
      title: p.title,
      description: p.excerpt,
      href: `/blog/${p.slug}`,
      keywords: `blog ${p.slug} ${p.title} ${p.excerpt} ${p.tags.join(" ")}`.toLowerCase(),
    });
  }

  for (const q of QUICK_ANSWERS) {
    entries.push({
      category: "FAQ",
      title: q.question,
      description: q.shortAnswer,
      href: `/ask#${q.id}`,
      keywords: `faq ask ${q.id} ${q.question} ${q.shortAnswer} ${q.category}`.toLowerCase(),
    });
  }

  return entries;
}

let CACHE: SearchEntry[] | null = null;
export function getSearchIndex(): SearchEntry[] {
  if (!CACHE) CACHE = build();
  return CACHE;
}

/** Score an entry against tokens. Higher = more relevant. */
function score(entry: SearchEntry, tokens: string[]): number {
  let s = 0;
  const title = entry.title.toLowerCase();
  const hay = entry.keywords;
  for (const t of tokens) {
    if (!t) continue;
    if (title === t) s += 100;
    if (title.startsWith(t)) s += 30;
    if (title.includes(t)) s += 20;
    // count occurrences in haystack
    const re = new RegExp(t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
    const matches = hay.match(re);
    if (matches) s += matches.length * 4;
  }
  return s;
}

export interface SearchHit extends SearchEntry {
  score: number;
}

export function searchAll(query: string, limit = 80): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  const idx = getSearchIndex();
  const hits: SearchHit[] = [];
  for (const e of idx) {
    const s = score(e, tokens);
    if (s > 0) hits.push({ ...e, score: s });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, limit);
}

export function groupByCategory(hits: SearchHit[]): Record<SearchCategory, SearchHit[]> {
  const groups = {} as Record<SearchCategory, SearchHit[]>;
  for (const h of hits) {
    (groups[h.category] ||= []).push(h);
  }
  return groups;
}

export const CATEGORY_ORDER: SearchCategory[] = [
  "Service", "Notary", "Visa", "Translate", "Apostille", "Embassy", "Marriage", "FAQ", "Blog", "Location", "Page",
];
